import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ParamMap, Params, Router, RouterLink } from '@angular/router';
import { distinctUntilChanged, map, Observable, switchMap } from 'rxjs';
import { ReportExportFormat } from '../reports/reports.store';
import { MaterialRequirementsState, MaterialRequirementsStore } from './material-requirements.store';
import { MaterialRequirementsDto, parentLabel, ROW_TYPE_LABELS } from './models/material-requirements.dto';
import { parseRange, ReportRange, sameRange } from '../../../utils/report-range';
import { plural } from '../../../utils/plural';
import { formatNumber } from '../../../utils/format-amount';
import { STRUCTURE_LABELS } from '../../../models/bom-structure';

/**
 * Raporty → Zapotrzebowanie materiałowe (0118): what Zgłoszenia is for planned amounts (0122) —
 * every line of every zlecenie planned in od–do, one row each, nothing summed, with the progress
 * log's toolbar and export. The range is the log's: same URL parameters, same default, carried
 * across by the tab links.
 */
@Component({
  selector: 'material-requirements-page',
  templateUrl: './material-requirements.page.html',
  styleUrl: './material-requirements.page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, RouterLink]
})
export class MaterialRequirementsPage implements OnInit, OnDestroy {
  readonly state$: Observable<MaterialRequirementsState>;

  formatNumber = formatNumber;
  parentLabel = parentLabel;

  readonly rowTypeLabels = ROW_TYPE_LABELS;
  readonly structureLabels = STRUCTURE_LABELS;

  constructor(
    private store: MaterialRequirementsStore,
    private route: ActivatedRoute,
    private router: Router,
    private destroyRef: DestroyRef
  ) {
    this.state$ = this.store.state$;
  }

  ngOnInit(): void {
    // The URL is the single source of truth for the range, exactly as on the log
    this.route.queryParamMap
      .pipe(
        map(params => {
          const range = parseRange(params);

          this.normalizeUrl(params, range);

          return range;
        }),
        distinctUntilChanged(sameRange),
        switchMap(range => this.store.load(range)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  onFromChange(from: string): void {
    this.navigate({ from: from || null });
  }

  onToChange(to: string): void {
    this.navigate({ to: to || null });
  }

  onFormatChange(format: string): void {
    this.store.setFormat(format as ReportExportFormat);
  }

  export(): void {
    this.store.export();
  }

  // "128 pozycji", the first half of the count line — the rows are no longer only materials (0122)
  rowCountLabel(count: number): string {
    return `${count} ${plural(count, 'pozycja', 'pozycje', 'pozycji')}`;
  }

  // "z 5 zleceń" — after z the genitive: z 1 zlecenia, z 2 zleceń
  orderCountLabel(count: number): string {
    return `z ${count} ${plural(count, 'zlecenia', 'zleceń', 'zleceń')}`;
  }

  // the numbers of the zlecenia the rows come from, for the count's tooltip
  orderNumbers(requirements: MaterialRequirementsDto): string {
    return requirements.orders.map(order => order.orderNumber).join(', ');
  }

  private normalizeUrl(params: ParamMap, range: ReportRange): void {
    if (params.get('from') !== range.from || params.get('to') !== range.to) {
      this.navigate(range);
    }
  }

  private navigate(queryParams: Params): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }
}
